import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authAPI } from '../../../../services/redwolfSdkAPI';
import { getToken } from '../../../../utils/localStorage';
import { passwordStrengthValidation } from '../../../../utils/validations';
import Modal from '../../../common/components/Modal';
import Spinner from '../../../common/components/Spinner';
import { useGoogleAnalytics } from '../../../common/hooks/useGoogleAnalytics';
import useRequestRedwolfService from '../../../common/hooks/useRequestRedwolfService';
import useTechnicalErrorMessage from '../../../common/hooks/useTechnicalErrorMessage';
import * as Sentry from '@sentry/react';
import { openNotification } from '../../../../store/commonSlice';
import { notification } from '../../../../utils/constants';

const AccountPassword = () => {
	const [isChangePasswordInfoModalOpen, setIsChangePasswordInfoModalOpen] = useState(false);
	const [updatePasswordForm, setUpdatePasswordForm] = useState({
		currentPassword: '',
		newPassword: ''
	});
	const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);
	const [isCurrentPasswordVisible, setIsCurrentPasswordVisible] = useState(false);
	const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const [requestRedwolfService] = useRequestRedwolfService();
	const dispatch = useDispatch();

	const [errorMsgs, setErrorMsgs] = useState({
		newPasswordErrorMessage: '',
		currentPasswordErrorMessage: ''
	});
	const [updatePasswordFormValid, setUpdatePasswordFormValid] = useState({
		isCurrentPasswordValid: true,
		isNewPasswordValid: true
	});
	const [passwordStrengthUpdateState, setPasswordStrengthUpdateState] = useState({
		isPasswordLengthPassed: false,
		isLowercasePassed: false,
		isUppercasePassed: false,
		isNumerPassed: false,
		isAllCasesPassed: false
	});
	const currentPasswordRef = useRef<HTMLInputElement>(null);
	const newPasswordRef = useRef<HTMLInputElement>(null);
	const { userProfile } = useSelector((state: any) => state.user);
	const token = getToken();

	const transaction = Sentry.startInactiveSpan({ name: 'Account Password' });

	const handleSubmit = (event: any) => {
		event.preventDefault();
		if (updatePasswordForm.currentPassword === updatePasswordForm.newPassword) {
			setErrorMsgs({
				...errorMsgs,
				newPasswordErrorMessage: 'Current Password & New Password must be different'
			});
			setUpdatePasswordFormValid({
				...updatePasswordFormValid,
				isNewPasswordValid: false,
				isCurrentPasswordValid: false
			});
			if (newPasswordRef.current !== null) {
				newPasswordRef.current.focus();
			}
		} else if (updatePasswordForm.currentPassword === '') {
			setErrorMsgs({
				...errorMsgs,
				currentPasswordErrorMessage: 'This is a required field.'
			});
			setUpdatePasswordFormValid({ ...updatePasswordFormValid, isCurrentPasswordValid: false });
			if (currentPasswordRef.current !== null) {
				currentPasswordRef.current.focus();
			}
		} else {
			setIsSpinnerLoading(true);
			const payAuthUpdatePasswordInfo = {
				oldPassword: updatePasswordForm.currentPassword,
				newPassword: updatePasswordForm.newPassword
			};

			logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_CHANGE_PASSWORD');

			requestRedwolfService(authAPI.updatePassword(token, payAuthUpdatePasswordInfo))
				.then((response: any) => {
					setIsSpinnerLoading(false);
					if (response.constructor.$metadata$.simpleName === 'Success') {
						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_CHANGE_PASSWORD_SUCCESS');
						handleHideModal();
						dispatch(
							openNotification({
								type: 'SUCCESS',
								heading: 'Success',
								body: notification.message.updatePasswordSuccess
							})
						);
					} else {
						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_CHANGE_PASSWORD_FAILURE', {
							error: 'failure'
						});

						if (response.response.detail === 'Password recently used. Use a different one.') {
							setErrorMsgs({ ...errorMsgs, newPasswordErrorMessage: response.response.detail });
							setUpdatePasswordFormValid({ ...updatePasswordFormValid, isNewPasswordValid: false });
							if (newPasswordRef.current !== null) {
								newPasswordRef.current.focus();
							}
						} else if (response.response.detail === 'Password is incorrect.') {
							setErrorMsgs({ ...errorMsgs, currentPasswordErrorMessage: response.response.detail });
							setUpdatePasswordFormValid({
								...updatePasswordFormValid,
								isCurrentPasswordValid: false
							});
							if (currentPasswordRef.current !== null) {
								currentPasswordRef.current.focus();
							}
						} else {
							dispatch(
								openNotification({
									type: 'ERROR',
									heading: 'Error',
									body: notification.message.updatePasswordError
								})
							);
						}
					}
					transaction && transaction.end();
				})
				.catch((err: any) => {
					transaction && transaction.end();
					showTechnicalErrorMessage();
					setIsSpinnerLoading(false);
					Sentry.captureException(err);
				});
		}
	};

	const validatePasswordOnBlur = (event: any) => {
		if (updatePasswordForm.currentPassword === '' && event.target.name === 'currentPassword') {
			setErrorMsgs({ ...errorMsgs, currentPasswordErrorMessage: 'This is a required field.' });
			setUpdatePasswordFormValid({ ...updatePasswordFormValid, isCurrentPasswordValid: false });
		} else if (updatePasswordForm.newPassword === '' && event.target.name === 'newPassword') {
			setErrorMsgs({ ...errorMsgs, newPasswordErrorMessage: 'This is a required field.' });
			setUpdatePasswordFormValid({ ...updatePasswordFormValid, isNewPasswordValid: false });
		} else if (
			!passwordStrengthUpdateState.isAllCasesPassed &&
			updatePasswordForm.newPassword !== ''
		) {
			setErrorMsgs({ ...errorMsgs, newPasswordErrorMessage: 'Password requirements not met.' });
			setUpdatePasswordFormValid({ ...updatePasswordFormValid, isNewPasswordValid: false });
		}
	};

	const handleModalOpen = () => {
		if (currentPasswordRef.current) {
			currentPasswordRef.current.classList.remove('active');
		}
		if (newPasswordRef.current) {
			newPasswordRef.current.classList.remove('active');
		}
		setIsCurrentPasswordVisible(false);
		setIsNewPasswordVisible(false);
		setIsChangePasswordInfoModalOpen(true);
	};

	const passwordStrengthHandler = (event: any) => {
		const { name, value } = event.target;
		setUpdatePasswordForm({ ...updatePasswordForm, [name]: value });

		if (name === 'newPassword') {
			const passwordStrengthUpdateState = passwordStrengthValidation(value);
			setPasswordStrengthUpdateState(passwordStrengthUpdateState);
			if (value) {
				setErrorMsgs({ ...errorMsgs, newPasswordErrorMessage: '' });
				setUpdatePasswordFormValid({ ...updatePasswordFormValid, isNewPasswordValid: true });
			}
		} else if (name === 'currentPassword' && value) {
			setErrorMsgs({ ...errorMsgs, currentPasswordErrorMessage: '' });
			setUpdatePasswordFormValid({ ...updatePasswordFormValid, isCurrentPasswordValid: true });
		}

		if (
			(name === 'currentPassword' || name === 'newPassword') &&
			value &&
			errorMsgs.newPasswordErrorMessage === 'Current Password & New Password must be different'
		) {
			setErrorMsgs({ ...errorMsgs, newPasswordErrorMessage: '' });
			setUpdatePasswordFormValid({ isCurrentPasswordValid: true, isNewPasswordValid: true });
		}
	};

	const handleHideModal = () => {
		setUpdatePasswordForm({
			currentPassword: '',
			newPassword: ''
		});
		setErrorMsgs({
			newPasswordErrorMessage: '',
			currentPasswordErrorMessage: ''
		});
		setPasswordStrengthUpdateState({
			isPasswordLengthPassed: false,
			isLowercasePassed: false,
			isUppercasePassed: false,
			isNumerPassed: false,
			isAllCasesPassed: false
		});
		setUpdatePasswordFormValid({ isCurrentPasswordValid: true, isNewPasswordValid: true });
		setIsChangePasswordInfoModalOpen(false);
	};

	const passwordShowHideToggle = (event: any) => {
		if (event.key === 'Enter') {
			if (event.target.id === 'new-password') {
				setIsNewPasswordVisible(!isNewPasswordVisible);
			} else {
				setIsCurrentPasswordVisible(!isCurrentPasswordVisible);
			}
		}
	};

	useEffect(() => {
		enableSaveButton();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updatePasswordFormValid]);

	const enableSaveButton = () => {
		if (
			updatePasswordFormValid.isCurrentPasswordValid &&
			updatePasswordFormValid.isNewPasswordValid &&
			passwordStrengthUpdateState.isAllCasesPassed
		) {
			setIsSaveButtonDisabled(true);
		} else {
			setIsSaveButtonDisabled(false);
		}
	};

	return (
		<div className="contact-info-area acc-pswd-ctr">
			<div className="contact-info-area-left">
				<h2 className="contact-info" id="edit-account-password">
					Account Password{' '}
				</h2>
				<div
					id="social-acc-div"
					style={userProfile.signInType === 'TRADITIONAL' ? { display: 'none' } : {}}>
					Signed in with social account. Password management disabled.
				</div>
			</div>
			<div className="contact-info-area-right">
				<button
					className="primary-button acc-info-edit"
					disabled={userProfile.signInType !== 'TRADITIONAL'}
					aria-describedby="edit-account-password"
					onClick={handleModalOpen}>
					Edit
				</button>

				<Modal
					containerId="accPassword"
					containerClasses="password-modal"
					show={isChangePasswordInfoModalOpen}
					onHide={handleHideModal}
					heading="Change Password">
					<form
						className="edit-modal-form update-pw-form update-pw-popup"
						id="submit_update_password_form">
						<ul className="form-list">
							<li className="fields">
								<div
									className={
										!updatePasswordFormValid.isCurrentPasswordValid ? 'field has-error' : 'field'
									}>
									<div
										className={
											isCurrentPasswordVisible
												? 'input-box form-group input-label pw-view'
												: 'input-box form-group input-label'
										}>
										<input
											name="currentPassword"
											type={isCurrentPasswordVisible ? 'text' : 'password'}
											className="form-control password"
											id="currentPassword"
											autoComplete="current-password"
											aria-required="true"
											aria-invalid="false"
											aria-describedby="current_pass_req"
											onChange={passwordStrengthHandler}
											onBlur={validatePasswordOnBlur}
											value={updatePasswordForm.currentPassword}
											ref={currentPasswordRef}
										/>
										<label htmlFor="currentPassword" className="control-label">
											Old Password
										</label>
										<code id="current_pass_req" aria-atomic="true" aria-live="assertive">
											{errorMsgs.currentPasswordErrorMessage}
										</code>
										<div
											className="input-field-icon"
											tabIndex={0}
											role="button"
											aria-label="Show password"
											id="old-password"
											onClick={() => setIsCurrentPasswordVisible(!isCurrentPasswordVisible)}
											onKeyDown={passwordShowHideToggle}>
											<em aria-hidden="true" className="icon-eye eye-icon"></em>
										</div>
									</div>
								</div>
							</li>
							<li className="fields">
								<div
									className={
										!updatePasswordFormValid.isNewPasswordValid ? 'field has-error' : 'field'
									}>
									<div
										className={
											isNewPasswordVisible
												? 'input-box form-group input-label pw-view'
												: 'input-box form-group input-label'
										}>
										<input
											name="newPassword"
											type={isNewPasswordVisible ? 'text' : 'password'}
											className="form-control password"
											id="newPassword"
											autoComplete="new-password"
											aria-required="true"
											aria-invalid="false"
											aria-describedby="old_new_diff"
											onChange={passwordStrengthHandler}
											onBlur={validatePasswordOnBlur}
											value={updatePasswordForm.newPassword}
											ref={newPasswordRef}
										/>
										<label htmlFor="newPassword" className="control-label">
											New Password
										</label>
										<code id="old_new_diff" aria-atomic="true" aria-live="assertive">
											{errorMsgs.newPasswordErrorMessage}
										</code>
										<div
											className="input-field-icon"
											tabIndex={0}
											role="button"
											aria-label="Show password"
											id="new-password"
											onClick={() => setIsNewPasswordVisible(!isNewPasswordVisible)}
											onKeyDown={passwordShowHideToggle}>
											<em aria-hidden="true" className="icon-eye eye-icon"></em>
										</div>
									</div>
								</div>
							</li>
						</ul>
						<div className="psw-info-component">
							<div className="psw-info">
								<p className="psw-title">Please Include:</p>
								<ul className="checklist-info">
									<li
										className={
											passwordStrengthUpdateState.isPasswordLengthPassed
												? 'item'
												: 'item incomplete'
										}>
										8<span aria-label="to">-</span>25 characters
									</li>
									<li
										className={
											passwordStrengthUpdateState.isLowercasePassed ? 'item' : 'item incomplete'
										}>
										1 lowercase letter
									</li>
									<li
										className={
											passwordStrengthUpdateState.isUppercasePassed ? 'item' : 'item incomplete'
										}>
										1 uppercase letter
									</li>
									<li
										className={
											passwordStrengthUpdateState.isNumerPassed ? 'item' : 'item incomplete'
										}>
										1 number
									</li>
								</ul>
							</div>
						</div>
						<div className="pswdsave-btnwrap">
							<button
								id="submit_button"
								type="submit"
								className="primary-button"
								onClick={handleSubmit}
								disabled={!isSaveButtonDisabled}>
								Save
							</button>
						</div>
						{isSpinnerLoading && <Spinner />}
					</form>
				</Modal>
			</div>
		</div>
	);
};

export default AccountPassword;
